import axios from "axios";
import { Accordion, AccordionTab } from "primereact/accordion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiSolidCheckCircle, BiSolidMinusCircle } from "react-icons/bi";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { useParams } from "react-router-dom";

import ImageViewer from "../../../components/ImageViewer";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { IDE_TYPE } from "../../../constants/common";
import useAuth from "../../../hooks/useAuth";
import { getLanguage } from "../../../utils/getLanguage";

const StudentQuestionsResults = ({ lessonName }) => {
  const { id: lessonID } = useParams();
  const lang = getLanguage();
  const { t } = useTranslation();
  const user = useAuth();
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(null);

  const fetchStudentQuestions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = user?.access_token;

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/student-questions/${lessonID}/${user.student_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            lang,
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      setQuestions(response?.data?.data);
      setStats(response?.data?.stats);
    } catch (error) {
      if (error.status == 400) {
        setError(t("student_not_solved_this_questions_after_reset"));
      } else {
        setError(t("failed_to_load_questions"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentQuestions();
  }, [lang]);

  const handleTabChange = (index) => {
    setActiveTabIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const renderOptions = (question, studentSubmission) => {
    // Get the student's selected answer(s)
    const studentAnswers = studentSubmission || [];

    return (
      <div
        className={`${question?.type?.id === 2 ? "my-3 mt-3 grid grid-cols-2 gap-4" : "my-3 mt-3 flex flex-col gap-4"} `}
      >
        {question.options?.map((option, index) => {
          // Check if the option text matches the student's answer text
          const studentAnswer = studentAnswers.find((answer) => {
            if (question?.type?.id === 2) {
              return answer.image === option.image;
            } else {
              return answer.text === option.text;
            }
          });
          const isStudentAnswer = studentAnswer !== undefined;
          const isCorrect = option.is_correct;

          return (
            <div
              key={index}
              className="relative flex w-fit cursor-pointer items-center gap-4 space-x-2 rounded-xl"
            >
              <input
                type="radio"
                name={`question-${question.id}`}
                value={option.text}
                checked={isStudentAnswer}
                className={`h-4 w-4 ${isCorrect ? "text-green-600" : isStudentAnswer ? "text-red-600" : "text-gray-800"}`}
              />
              <div
                className={`font-bold ${
                  isCorrect
                    ? "rounded-xl border-[#3DFF0A] p-4 ring-2 ring-[#3DFF0A]" // Highlight correct answers
                    : isStudentAnswer
                      ? "rounded-xl border-red-500 p-4 ring-2 ring-red-300" // Highlight incorrect answers
                      : "p-4 text-gray-800 ring-2 ring-transparent" // Default color for unselected answers
                }`}
              >
                {question?.type?.id === 1 ? (
                  user.IDEType == IDE_TYPE.WEB ? (
                    <div className="py-4 text-sm font-medium text-gray-800 md:text-xl">{option.text}</div>
                  ) : (
                    <div
                      className="py-4 text-sm font-medium text-gray-800 md:text-xl"
                      dangerouslySetInnerHTML={{ __html: option.text }}
                    ></div>
                  )
                ) : (
                  <img
                    src={option.image}
                    alt={`Option ${option.id}`}
                    className="h-36 w-36 rounded-xl object-contain"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4 p-4">
      {/* Assessment Box */}
      <div>
        <h2 className="mb-4 text-xl font-bold text-blue-500 sm:mb-6 sm:text-3xl">
          {lessonName} <span className="ml-1 font-bold text-green-500 sm:ml-2">{t("done_2")}!</span>
        </h2>

        <div className="grid grid-cols-1 gap-y-4 sm:gap-y-6 md:grid-cols-2 md:gap-x-8 lg:grid-cols-3">
          {/* Earned Points */}
          <div>
            <p className="text-base font-medium text-blue-500 sm:text-lg">
              {t("earned_points")}{" "}
              <span className="ml-1 font-semibold text-green-500">
                {stats?.earned_points} {t("pts")}
              </span>
            </p>
          </div>

          {/* Correct Answers */}
          <div>
            <p className="text-base font-medium text-blue-500 sm:text-lg">
              {t("correct_answers")}{" "}
              <span className="ml-1 font-semibold text-green-500">
                {stats?.correct_answers}/{stats?.total_questions}
              </span>
            </p>
          </div>

          {/* Gained Skills */}
          <div>
            <p className="text-base font-medium text-blue-500 sm:text-lg">
              {t("skills_score")}{" "}
              <span className="ml-1 font-semibold text-green-500">
                {stats?.gain_skills}/{stats?.total_skills}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Questions results */}
      <div>
        <h1 className="mb-2 mt-8 text-2xl font-semibold text-blue-500">{t("assessment_results")}</h1>
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : questions?.length === 0 ? (
          <p>{t("no_questions_available")}</p>
        ) : (
          questions?.length > 0 && (
            <Accordion>
              {questions.map((questionItem, index) => (
                <AccordionTab
                  key={index}
                  pt={{
                    headericon: "hidden",
                    headeraction: "bg-transparent p-0",
                    content: "p-0 pt-5 bg-transparent",
                  }}
                  header={
                    <div
                      className={`flex flex-col items-start gap-2 rounded-2xl sm:flex-row sm:items-center sm:justify-between sm:gap-2 ${
                        activeTabIndex === index
                          ? "bg-blue-500 text-white"
                          : "border border-blue-500 bg-transparent text-blue-500"
                      } p-4`}
                      onClick={() => handleTabChange(index)}
                    >
                      {/* Skill name */}
                      <span className="flex-1 text-start text-sm sm:text-base">
                        {questionItem?.skill?.name}
                      </span>

                      {/* Questions count */}
                      <span className="text-xs font-light sm:text-sm">
                        {questionItem?.questions?.length} {t("questions")}
                      </span>

                      {/* Earned points */}
                      <span className="text-xs sm:text-sm">
                        {t("your_earned_points")}{" "}
                        <span className={`${activeTabIndex === index ? "text-white" : "text-green-500"}`}>
                          {questionItem?.score?.percentage} {t("pts")}
                        </span>
                      </span>

                      {/* Icon */}
                      <span className="flex items-center sm:ml-4">
                        {activeTabIndex === index ? (
                          <MdKeyboardArrowUp
                            size={20}
                            className={`${activeTabIndex === index ? "text-white" : "text-blue-500"}`}
                          />
                        ) : (
                          <MdKeyboardArrowDown size={20} className="text-blue-500" />
                        )}
                      </span>
                    </div>
                  }
                >
                  {questionItem.questions.map((question, idx) => {
                    const submission = questionItem.student_submission?.[idx];
                    return (
                      <div
                        key={idx}
                        className="mb-6 rounded-2xl border bg-white p-6 shadow-lg dark:bg-dark-background"
                      >
                        <div>
                          <div className="flex items-center space-x-2">
                            {user.IDEType == IDE_TYPE.WEB ? (
                              <p className="text-start text-sm font-semibold text-blue-500 md:text-xl">
                                {question.text}
                              </p>
                            ) : (
                              <p
                                className="text-start text-sm font-semibold text-blue-500 md:text-xl"
                                dangerouslySetInnerHTML={{ __html: question.text }}
                              ></p>
                            )}
                          </div>
                          {question?.image && <ImageViewer src={question.image} className="mb-4" />}
                        </div>
                        {renderOptions(question, questionItem.student_submission)}
                        <div className="flex items-center space-x-2">
                          {submission?.is_correct ? (
                            <>
                              <p className="text-sm font-medium text-green-600 md:text-xl">
                                {t("your_answer_is_correct")}
                              </p>
                              <BiSolidCheckCircle size={24} className="text-green-500" />
                            </>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-red-600 md:text-xl">
                                {t("your_answer_is_incorrect")}
                              </p>
                              <BiSolidMinusCircle size={24} className="text-red-500" />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </AccordionTab>
              ))}
            </Accordion>
          )
        )}
      </div>
    </div>
  );
};

export default StudentQuestionsResults;
